'use client';

import {
  AttributeSide,
  AttributeSideDynamic,
} from '@eliadmin/core/src/components/categories/attributes/attribute';
import { AttributeCategory } from '@eliadmin/core/src/components/categories/attributes/attribute-category';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Card } from '@eliadmin/core/src/components/ui/card';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@eliadmin/core/src/components/ui/sheet';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { FiArrowLeft } from 'react-icons/fi';
import { LuX } from 'react-icons/lu';
import { MdFilterList } from 'react-icons/md';

const FilterSeparator = () => <Separator className="w-auto bg-[#D4D4D4] last-of-type:hidden" />;

export function CategoryFilterSheet() {
  const templateSettings = useGlobalStore(state => state.templateSettings);
  const categoryTree = useCategoryStore(state => state.categoryTree);
  const category = useCategoryStore(state => state.category);
  const { t } = useTranslations();
  const clearFilter = useCategoryStore(s => s.clearFilters);

  const attributes = category.attributes?.filter(
    attribute => attribute.name && attribute.values?.length,
  );

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button className="flex h-12 w-full items-center justify-center gap-2 uppercase sm:h-[52px] sm:w-fit">
          <MdFilterList size={24} /> {t('text174')}
        </Button>
      </SheetTrigger>

      <SheetContent
        overlayClassName="bg-black/80 backdrop-blur-none"
        className="w-full max-w-[402px] border-none bg-white p-0 shadow-none sm:max-w-[402px]"
        showCloseIcon={false}
        aria-describedby={undefined}
      >
        <SheetTitle className="sr-only">{t('text174')}</SheetTitle>
        {/* Full-height flex column container */}
        <Card
          className={cn(
            'relative h-dvh max-h-dvh bg-white shadow-none',
            'flex flex-col',
            'ea-category-filter-sheet',
          )}
        >
          {/* Header (non-scrolling) */}
          <div
            className={cn(
              'flex w-full flex-row items-start justify-between p-4',
              'ea-category-filter-sheet__expand-button',
            )}
          >
            <p className="font-oswald text-xl font-medium uppercase text-black sm:text-2xl lg:text-3xl">
              FILTROVAŤ
            </p>
            <SheetClose aria-label={t('text1855')}>
              <LuX className="text-black" size={24} />
            </SheetClose>
          </div>

          {/* Content (scrolling area) */}
          <div
            className={cn(
              'flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-white px-4 pb-2',
              'ea-category-filter-sheet__content',
            )}
          >
            <FilterSeparator />

            {!!templateSettings.category?.tree_list?.enabled && !!categoryTree && (
              <AttributeCategory order={templateSettings.category.tree_list.order} />
            )}

            <FilterSeparator />

            {!!templateSettings.category?.categories_in_filter?.enabled &&
              !!category.sub_categories?.length && (
                <>
                  <AttributeSide
                    name={t('text337')}
                    type="subCategory"
                    attribute={{ type: 'status', type2: 'statuses' }}
                    order={templateSettings.category.categories_in_filter.order}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__subcategories') }}
                  />
                  <FilterSeparator />
                </>
              )}

            {!!templateSettings.category?.manufacturers_in_filter?.enabled &&
              !!category.manufacturers?.length && (
                <>
                  <AttributeSide
                    name={t('text195')}
                    type="generic"
                    attribute={{ type: 'manufacturer', type2: 'manufacturers' }}
                    order={templateSettings.category.manufacturers_in_filter.order}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__manufacturers') }}
                  />
                  <FilterSeparator />
                </>
              )}

            {!!templateSettings.category?.statuses_in_filter?.enabled &&
              !!category.statuses?.length && (
                <>
                  <AttributeSide
                    name={t('text518')}
                    type="generic"
                    attribute={{ type: 'status', type2: 'statuses' }}
                    order={templateSettings.category?.categories_in_filter?.order}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__statuses') }}
                  />
                  <FilterSeparator />
                </>
              )}

            {!!templateSettings.category?.availability_in_filter?.enabled &&
              !!category.availabilities?.length && (
                <>
                  <AttributeSide
                    name={t('text339')}
                    type="generic"
                    attribute={{ type: 'availability', type2: 'availabilities' }}
                    order={templateSettings.category.availability_in_filter.order}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__availabilities') }}
                  />
                  <FilterSeparator />
                </>
              )}

            {!!templateSettings.category?.attributes_in_filter?.enabled && (
              <>
                {attributes?.map((attribute, i) => (
                  <div key={`attribute-${attribute.id ?? attribute.name}-${i}`}>
                    <AttributeSideDynamic
                      name={attribute.name}
                      attribute={attribute as any}
                      className={{ wrapper: cn('', 'ea-category-filter-sheet__attributes') }}
                    />
                    <FilterSeparator />
                  </div>
                ))}
              </>
            )}

            {!!templateSettings.category?.price_in_filter?.enabled && (
              <>
                <AttributeSide
                  name={t('text340')}
                  type="price"
                  attribute={{ type: 'status', type2: 'statuses' }}
                  order={templateSettings.category.price_in_filter.order}
                  className={{ wrapper: cn('', 'ea-category-filter-sheet__price') }}
                />
                <FilterSeparator />
              </>
            )}

            {!!templateSettings.category?.suppliers_in_filter?.enabled && (
              <>
                <AttributeSide
                  name={t('text718')}
                  type="generic"
                  attribute={{ type: 'supplier', type2: 'suppliers' }}
                  className={{ wrapper: cn('', 'ea-category-filter-sheet__suppliers') }}
                  order={templateSettings.category.suppliers_in_filter.order}
                />
                <FilterSeparator />
              </>
            )}

            {!!templateSettings.category?.suppliers_location_in_filter?.enabled && (
              <>
                <AttributeSide
                  name={t('text728')}
                  type="supplierLocation"
                  attribute={{ type: 'supplier', type2: 'suppliers' }}
                  className={{ wrapper: cn('', 'ea-category-filter-sheet__suppliers_location') }}
                  order={templateSettings.category?.suppliers_in_filter?.order}
                />
                <FilterSeparator />
              </>
            )}
          </div>

          <div
            className={cn(
              'shrink-0 border-t bg-white p-4',
              'ea-category-filter-sheet__actions-wrapper',
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <Button className="w-full whitespace-nowrap uppercase">{t('text634')}</Button>
              <Button
                className="w-full whitespace-nowrap uppercase"
                variant="dark"
                onClick={clearFilter}
              >
                {t('text1611')}
              </Button>
            </div>
          </div>
        </Card>
      </SheetContent>
    </Sheet>
  );
}
